/**
 * Launch Readiness types — frontend mirror of the Builder Service response
 * shape produced by
 * `src/modules/builder-service/src/services/launch/LaunchReadinessService.ts`
 * (J2-I0). Keep field names in sync with the backend; the frontend never
 * derives its own readiness verdicts.
 */

export type LaunchGoalSet = 'first_value' | 'monetized' | 'ads_ready' | 'full_production';

export type LaunchMilestoneStatus = 'done' | 'missing' | 'human_decision' | 'check_unavailable' | 'not_evaluated';

export interface LaunchMilestoneNextAction {
  label: string;
  /** In-app route (react-router path), e.g. `/project/{pid}/integrations`. */
  deepLink: string;
}

/** Raw CLI preflight row a milestone consumed (parity surface, display-optional). */
export interface LaunchPreflightCheckProjection {
  checkId: string;
  status: string;
  label?: string;
  deprecated?: boolean;
}

export interface LaunchMilestone {
  id: string;
  title: string;
  goalSets: LaunchGoalSet[];
  status: LaunchMilestoneStatus;
  detail: string;
  /** Every 'missing' milestone carries exactly ONE next action. */
  nextAction?: LaunchMilestoneNextAction;
  /** Optional alternative connection route for the payments milestone. */
  secondaryAction?: LaunchMilestoneNextAction;
  sources?: LaunchPreflightCheckProjection[];
}

export interface LaunchGoalReadiness {
  goal: LaunchGoalSet;
  done: number;
  missing: number;
  humanDecision: number;
  checkUnavailable: number;
  notEvaluated: number;
  total: number;
  /** Human decisions never block goal readiness. */
  ready: boolean;
}

export interface LaunchReadinessSummary {
  done: number;
  missing: number;
  humanDecision: number;
  checkUnavailable: number;
  notEvaluated: number;
  total: number;
}

export type LaunchReadinessStatus = 'ready' | 'incomplete' | 'degraded';

export interface LaunchReadinessData {
  checkedAt: string;
  projectId: string;
  status: LaunchReadinessStatus;
  summary: LaunchReadinessSummary;
  goals: LaunchGoalReadiness[];
  milestones: LaunchMilestone[];
}

/** Milestone id of the first-party analytics opt-in trap. */
export const FIRST_PARTY_OPT_IN_MILESTONE_ID = 'analytics.firstPartyOptIn';

/**
 * The exact warning sentence the backend bakes into the first-party opt-in
 * missing detail (LaunchReadinessService.FIRST_PARTY_OPT_IN_WARNING).
 */
export const FIRST_PARTY_OPT_IN_WARNING = 'Reports will stay empty until first-party analytics opt-in is enabled';


/** Payments milestone whose sources are projected into display checks. */
export const PAYMENTS_MILESTONE_ID = 'payments.paywallBinding';

export const MILESTONE_CHECK_LABELS: Readonly<Record<string, string>> = {
  'paywall.present': 'Paywall present',
  'paywall.productsResolved': 'Products resolved',
  'payments.provider.connected': 'Test-mode provider connected',
  'flow.emailCapture': 'Email capture',
};
